import React, { useEffect } from 'react'
import Snackbar from '@mui/material/Snackbar'
import { CheckCircle, Warning, X, XCircle } from '@phosphor-icons/react'

const TOAST_VARIANTS = {
  success: {
    title: 'Acción completada',
    Icon: CheckCircle,
    iconBackground: '#E8F7EE',
    progress: '#168F56'
  },
  warning: { title: 'Atención', Icon: Warning, iconBackground: '#FFF4DB', progress: '#B7791F' },
  error: {
    title: 'Ocurrió un error',
    Icon: XCircle,
    iconBackground: '#FDECEC',
    progress: '#D14343'
  }
}

export const UBIQUA_TOWER_TOAST_DEFAULT_DURATION_MS = 3800

/**
 * Toast tower reutilizable (AssignRbacRolesPage).
 *
 * Ejemplo:
 * <UbiquaTowerToastAlert
 *   open={Boolean(alert)}
 *   id={alert?.id}
 *   title="Acción completada"
 *   message="Los cambios se guardaron correctamente."
 *   icon={<CheckCircle size={18} weight="regular" aria-hidden="true" />}
 *   colors={{ iconBackground: '#E8F7EE', progress: '#168F56' }}
 *   onClose={() => setAlert(null)}
 * />
 */
export default function UbiquaTowerToastAlert({
  id,
  open = true,
  severity = 'success',
  title,
  message,
  text,
  icon,
  colors = {},
  onClose,
  durationMs = UBIQUA_TOWER_TOAST_DEFAULT_DURATION_MS,
  showCountdown = true,
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  className = '',
  snackbarClassName = '',
  snackbarSx = {}
}) {
  const bodyText = message ?? text
  const variant = TOAST_VARIANTS[severity] || TOAST_VARIANTS.error
  const VariantIcon = variant.Icon
  const resolvedColors = { ...variant, ...colors }

  useEffect(() => {
    if (!open || !bodyText || typeof onClose !== 'function') return undefined

    const timeoutId = window.setTimeout(() => {
      onClose()
    }, durationMs)

    return () => window.clearTimeout(timeoutId)
  }, [bodyText, durationMs, id, onClose, open])

  if (!open || !bodyText) return null

  return (
    <Snackbar
      open={open}
      key={id}
      className={['ubiqua-tower-toast-snackbar', snackbarClassName].filter(Boolean).join(' ')}
      onClose={(_, reason) => {
        if (reason === 'clickaway') return
        onClose?.()
      }}
      anchorOrigin={anchorOrigin}
      autoHideDuration={durationMs}
      sx={{
        left: 'auto',
        width: 'auto',
        maxWidth: 'calc(100vw - 32px)',
        ...snackbarSx
      }}
    >
      <div
        id={id}
        role="alert"
        aria-live="polite"
        className={['ubiqua-tower-toast', className].filter(Boolean).join(' ')}
        style={{
          '--ubiqua-tower-toast-icon-bg': resolvedColors.iconBackground,
          '--ubiqua-tower-toast-progress': resolvedColors.progress,
          '--ubiqua-tower-toast-duration': `${durationMs}ms`
        }}
      >
        <div className="ubiqua-tower-toast__icon">
          {icon ?? (
            <VariantIcon
              size={20}
              weight="regular"
              color={resolvedColors.progress}
              aria-hidden="true"
            />
          )}
        </div>
        <div className="ubiqua-tower-toast__body">
          {(title ?? variant.title) ? (
            <strong className="ubiqua-tower-toast__title">{title ?? variant.title}</strong>
          ) : null}
          <span className="ubiqua-tower-toast__message">{bodyText}</span>
        </div>
        {typeof onClose === 'function' ? (
          <button
            type="button"
            className="ubiqua-tower-toast__close"
            aria-label="Cerrar alerta"
            onClick={onClose}
          >
            <X size={16} weight="bold" aria-hidden="true" />
          </button>
        ) : null}
        {showCountdown ? (
          <span className="ubiqua-tower-toast__countdown" aria-hidden="true" />
        ) : null}
      </div>
    </Snackbar>
  )
}
